import type { VisionPlan, VisionPoint, VisionRecipe } from "@sitescene/types";

import { VisionCountMode } from "@sitescene/types";
import { useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { DetailPanel } from "@/components/detail-panel";
import { StatusMessage } from "@/components/status-message";
import { Button } from "@/components/ui/buttons/button";
import { Checkbox } from "@/components/ui/checkbox/checkbox";
import { Input } from "@/components/ui/input/input";
import { NativeSelect } from "@/components/ui/select/select-native";
import { TextArea } from "@/components/ui/textarea/textarea";
import {
	useGoldVideos,
	useGoldVideoUrl,
} from "@/features/gold-videos/use-gold-videos";
import {
	BoxOverlay,
	type OverlayShape,
} from "@/features/vision-lab/box-overlay";
import { formatSeconds, planIsRunnable } from "@/features/vision-lab/format";
import {
	isRunOpen,
	usePlanInstruction,
	useRecipes,
	useRunBoxes,
	useSaveRecipe,
	useStartRun,
	useVisionRun,
	useVisionRuns,
} from "@/features/vision-lab/use-vision-lab";
import { formatWhen } from "@/pages/plausibility/format";
import { cx } from "@/utils/cx";

import { GpuChip } from "./GpuChip";
import { type DrawMode, PlanEditor } from "./PlanEditor";
import { RunPanel } from "./RunPanel";

/**
 * Ask a gold standard video a question. Gemini turns the instruction into a
 * plan (what RF-DETR looks for, what's counted, what Gemini describes or
 * looks for itself), which can be edited, saved as a recipe, and run on the
 * GPU. The boxes play over the video and the answer comes back beside it.
 */
export const VisionLab = () => {
	const [params, setParams] = useSearchParams();
	const videoId = params.get("video") ?? undefined;
	const videos = useGoldVideos();
	const video = videos.data?.find((candidate) => candidate.id === videoId);
	const playback = useGoldVideoUrl(videoId);
	const videoRef = useRef<HTMLVideoElement>(null);

	const [instruction, setInstruction] = useState("");
	const [plan, setPlan] = useState<null | VisionPlan>(null);
	const [recipe, setRecipe] = useState<null | VisionRecipe>(null);
	const [drawing, setDrawing] = useState<DrawMode | null>(null);
	const [pending, setPending] = useState<VisionPoint[]>([]);
	const [pickedRunId, setPickedRunId] = useState<string | undefined>();
	const [hidden, setHidden] = useState<Set<string>>(new Set());
	const [selectedId, setSelectedId] = useState<number | undefined>();

	const planning = usePlanInstruction();
	const start = useStartRun();
	const runs = useVisionRuns(videoId);
	const runId = pickedRunId ?? runs.data?.[0]?.id;
	const run = useVisionRun(runId);
	const boxes = useRunBoxes(run.data);

	// Memoized by the React Compiler
	const objects = run.data?.objects ?? [];
	const classOf = new Map(objects.map((object) => [object.id, object.class]));
	const counted = new Set(
		objects.filter((object) => object.counted).map((object) => object.id),
	);
	const labels = new Map(
		objects.map((object) => [
			object.id,
			`#${object.id} ${object.class}${object.description ? ` · ${object.description}` : ""}`,
		]),
	);
	const runClasses = [...new Set(objects.map((object) => object.class))].sort();

	// The line or area, and the focus point: being drawn, else the plan's,
	// else the shown run's
	const shownPlan = plan ?? run.data?.plan;
	const shownCount = drawing ? undefined : shownPlan?.count;
	const shownPoint = drawing === "point" ? undefined : shownPlan?.focusPoint;
	const shape: OverlayShape | undefined =
		drawing && drawing !== "point"
			? { pending, point: shownPoint }
			: shownCount || shownPoint
				? { area: shownCount?.area, line: shownCount?.line, point: shownPoint }
				: undefined;

	const pickVideo = (id: string) => {
		setParams(id ? { video: id } : {});
		setPickedRunId(undefined);
		setSelectedId(undefined);
		setDrawing(null);
	};

	const placePoint = (point: VisionPoint) => {
		if (!plan || !drawing) return;
		if (drawing === "point") {
			setPlan({ ...plan, focusPoint: point });
			setDrawing(null);
			return;
		}
		if (!plan.count) return;
		const next = [...pending, point];
		if (drawing === "line" && next.length === 2) {
			const [a, b] = next as [VisionPoint, VisionPoint];
			setPlan({
				...plan,
				count: { ...plan.count, line: { a, b }, mode: VisionCountMode.Line },
			});
			setDrawing(null);
			setPending([]);
			return;
		}
		setPending(next);
	};

	const finishArea = () => {
		if (plan?.count && pending.length >= 3) {
			setPlan({
				...plan,
				count: { ...plan.count, area: pending, mode: VisionCountMode.Area },
			});
		}
		setDrawing(null);
		setPending([]);
	};

	const makePlan = () => {
		if (!videoId || !instruction.trim()) return;
		setRecipe(null);
		planning.mutate(
			{ instruction: instruction.trim(), videoId },
			{ onSuccess: (planned) => setPlan(planned) },
		);
	};

	const runPlan = () => {
		if (!videoId || !plan) return;
		start.mutate(
			{
				instruction: instruction.trim() || recipe?.instruction || "Run recipe",
				plan: {
					...plan,
					...(plan.geminiTargets && {
						geminiTargets: plan.geminiTargets.filter((target) =>
							target.label.trim(),
						),
					}),
				},
				recipeId: recipe?.id,
				videoId,
			},
			{
				onSuccess: (created) => {
					setPickedRunId(created.id);
					setSelectedId(undefined);
				},
			},
		);
	};

	return (
		<div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
			<div className="flex flex-col gap-3 xl:col-span-3">
				<NativeSelect
					label="Gold standard video"
					onChange={(event) => pickVideo(event.target.value)}
					options={[
						{
							label: videos.isLoading ? "Loading videos…" : "Pick a video…",
							value: "",
						},
						...(videos.data ?? []).map((candidate) => ({
							label: `${candidate.title || candidate.filename} (${formatSeconds(candidate.durationSeconds)})`,
							value: candidate.id,
						})),
					]}
					value={videoId ?? ""}
				/>

				<div className="relative aspect-video w-full overflow-hidden rounded-lg bg-gray-900">
					{playback.data ? (
						<>
							<video
								className="h-full w-full object-contain"
								controls
								preload="metadata"
								ref={videoRef}
								src={playback.data}
							/>
							<BoxOverlay
								classOf={classOf}
								counted={counted}
								detections={boxes.data?.detections}
								geminiBoxes={boxes.data?.geminiBoxes}
								hiddenClasses={hidden}
								labels={labels}
								onPoint={drawing ? placePoint : undefined}
								selectedId={selectedId}
								shape={shape}
								videoRef={videoRef}
							/>
						</>
					) : (
						<p className="flex h-full items-center justify-center px-6 text-center text-sm text-gray-400">
							{videoId
								? "Loading the video…"
								: "Pick a gold standard video to start."}
						</p>
					)}
				</div>

				{video?.description && (
					<p className="text-sm text-secondary">
						<span className="text-tertiary">Ground truth: </span>
						{video.description}
					</p>
				)}

				{drawing === "area" && (
					<div className="flex items-center gap-2 text-sm">
						<span className="text-tertiary">
							{pending.length} corners placed.
						</span>
						<Button
							isDisabled={pending.length < 3}
							onClick={finishArea}
							size="xs"
						>
							Finish
						</Button>
						<Button color="secondary" onClick={() => setPending([])} size="xs">
							Clear
						</Button>
					</div>
				)}

				{runClasses.length > 0 && (
					<div className="flex flex-wrap items-center gap-3 text-sm">
						<span className="text-tertiary">Show:</span>
						{runClasses.map((name) => (
							<Checkbox
								isSelected={!hidden.has(name)}
								key={name}
								label={name}
								onChange={(on) =>
									setHidden((current) => {
										const next = new Set(current);
										if (on) next.delete(name);
										else next.add(name);
										return next;
									})
								}
							/>
						))}
						{boxes.data && boxes.data.frameStride > 1 && (
							<span className="text-tertiary">
								(every{" "}
								{boxes.data.frameStride === 2
									? "second"
									: `${boxes.data.frameStride}th`}{" "}
								frame, to fit)
							</span>
						)}
					</div>
				)}

				{run.data && (
					<RunPanel
						onSelect={(id, seconds) => {
							setSelectedId(id);
							if (videoRef.current) {
								videoRef.current.currentTime = seconds;
								videoRef.current.pause();
							}
						}}
						run={run.data}
						selectedId={selectedId}
					/>
				)}

				{videoId && (
					<DetailPanel title="Runs on this video">
						<StatusMessage
							empty="No runs yet."
							error={runs.error}
							isEmpty={!runs.isLoading && !runs.data?.length}
							isLoading={runs.isLoading}
						/>
						<ul className="flex flex-col gap-1">
							{(runs.data ?? []).map((entry) => (
								<li key={entry.id}>
									<button
										className={cx(
											"flex w-full flex-col rounded px-2 py-1 text-left text-sm hover:bg-brand-50",
											entry.id === runId && "bg-brand-50",
										)}
										onClick={() => {
											setPickedRunId(entry.id);
											setSelectedId(undefined);
										}}
										type="button"
									>
										<span className="text-primary">{entry.instruction}</span>
										<span className="text-xs text-tertiary">
											{formatWhen(entry.createdAt)} ·{" "}
											{isRunOpen(entry) ? "running" : entry.status}
										</span>
									</button>
								</li>
							))}
						</ul>
					</DetailPanel>
				)}
			</div>

			<div className="flex flex-col gap-4 xl:col-span-2">
				<GpuChip />

				<DetailPanel title="Ask">
					<div className="flex flex-col gap-3">
						<TextArea
							aria-label="Instruction"
							onChange={setInstruction}
							placeholder="How many people walk past? Identify the cars. Who is carrying a bag?"
							rows={3}
							value={instruction}
						/>
						<div className="flex flex-wrap items-center gap-2">
							<Button
								isDisabled={
									!videoId ||
									instruction.trim().length < 3 ||
									planning.isPending
								}
								onClick={makePlan}
								size="sm"
							>
								{planning.isPending ? "Planning…" : "Make a plan"}
							</Button>
							<RecipePicker
								onPick={(picked) => {
									setRecipe(picked);
									setPlan(picked.plan);
									setInstruction(picked.instruction);
								}}
							/>
						</div>
						{planning.error && (
							<p className="text-sm text-error-primary" role="alert">
								{planning.error.message}
							</p>
						)}
					</div>
				</DetailPanel>

				{plan && (
					<DetailPanel title={recipe ? `Plan: ${recipe.name}` : "Plan"}>
						<PlanEditor
							drawing={drawing}
							onChange={setPlan}
							onDraw={(mode) => {
								setDrawing(mode);
								setPending([]);
							}}
							plan={plan}
						/>
						<div className="mt-4 flex flex-col gap-3 border-t border-secondary pt-4">
							<Button
								isDisabled={
									!videoId || !planIsRunnable(plan) || start.isPending
								}
								onClick={runPlan}
								size="sm"
							>
								{start.isPending ? "Starting…" : "Run"}
							</Button>
							{start.error && (
								<p className="text-sm text-error-primary" role="alert">
									{start.error.message}
								</p>
							)}
							<SaveRecipe
								instruction={instruction}
								onSaved={setRecipe}
								plan={plan}
								recipe={recipe}
							/>
						</div>
					</DetailPanel>
				)}
			</div>
		</div>
	);
};

const RecipePicker = ({
	onPick,
}: {
	onPick: (recipe: VisionRecipe) => void;
}) => {
	const recipes = useRecipes();
	if (!recipes.data?.length) return null;
	return (
		<NativeSelect
			aria-label="Load a recipe"
			onChange={(event) => {
				const picked = recipes.data?.find(
					(entry) => entry.id === event.target.value,
				);
				if (picked) onPick(picked);
			}}
			options={[
				{ label: "Or load a recipe…", value: "" },
				...recipes.data.map((entry) => ({
					label: entry.name,
					value: entry.id,
				})),
			]}
			value=""
		/>
	);
};

/** Save the plan as a new recipe, or save changes to the one it came from. */
const SaveRecipe = ({
	instruction,
	onSaved,
	plan,
	recipe,
}: {
	instruction: string;
	onSaved: (recipe: VisionRecipe) => void;
	plan: VisionPlan;
	recipe: null | VisionRecipe;
}) => {
	const save = useSaveRecipe();
	const [name, setName] = useState("");
	const text = instruction.trim() || recipe?.instruction || "";

	return (
		<div className="flex flex-col gap-2">
			<div className="flex flex-wrap items-end gap-2">
				{recipe && (
					<Button
						color="secondary"
						isDisabled={save.isPending}
						onClick={() =>
							save.mutate(
								{ id: recipe.id, instruction: text, name: recipe.name, plan },
								{ onSuccess: onSaved },
							)
						}
						size="sm"
					>
						Save changes to "{recipe.name}"
					</Button>
				)}
				<Input
					className="w-48"
					onChange={setName}
					placeholder="Recipe name"
					value={name}
				/>
				<Button
					color="secondary"
					isDisabled={!name.trim() || text.length < 3 || save.isPending}
					onClick={() =>
						save.mutate(
							{ instruction: text, name: name.trim(), plan },
							{
								onSuccess: (saved) => {
									setName("");
									onSaved(saved);
								},
							},
						)
					}
					size="sm"
				>
					Save as recipe
				</Button>
			</div>
			{save.error && (
				<p className="text-sm text-error-primary" role="alert">
					{save.error.message}
				</p>
			)}
		</div>
	);
};
